/**
 * Shared photo URL utilities
 *
 * Consolidates photo URL logic used across the application:
 * - getBestPhotoUrl: Selects best available photo URL (large > thumbnail > original)
 * - addSignedUrlsToPhotos: Batch signs storage URLs
 * - normalizePath: Cleans storage paths
 */

import { createSignedStorageUrls } from './storage';

// ============================================================================
// PHOTO URL SELECTION
// ============================================================================

/**
 * Selects the best available photo URL from the three sizes
 *
 * Priority order: large > thumbnail > original
 * This ensures the best quality image is shown while falling back
 * to smaller sizes if the preferred size is not available.
 *
 * @param large - URL for large photo variant
 * @param thumbnail - URL for thumbnail photo variant
 * @param original - URL for original photo
 * @returns The best available URL, or null if none available
 *
 * @example
 * // Using with photo object
 * const photoUrl = getBestPhotoUrl(
 *   photo.large_photo_url,
 *   photo.thumbnail_photo_url,
 *   photo.original_photo_url
 * );
 *
 * // Using with MemberPhoto type
 * const url = photo ? getBestPhotoUrl(
 *   photo.large_photo_url,
 *   photo.thumbnail_photo_url,
 *   photo.original_photo_url
 * ) : null;
 */
export function getBestPhotoUrl(
  large?: string | null,
  thumbnail?: string | null,
  original?: string | null
): string | null {
  if (large) return large;
  if (thumbnail) return thumbnail;
  if (original) return original;
  return null;
}

/**
 * Helper to get best photo URL from a photo object
 *
 * @param photo - Photo object with URL fields (or null/undefined)
 * @returns The best available URL, or null if no photo
 *
 * @example
 * const url = getBestPhotoUrlFromObject(member.webcam_photo);
 */
export function getBestPhotoUrlFromObject(
  photo: { large_photo_url?: string | null; thumbnail_photo_url?: string | null; original_photo_url?: string | null } | null | undefined
): string | null {
  if (!photo) return null;
  return getBestPhotoUrl(photo.large_photo_url, photo.thumbnail_photo_url, photo.original_photo_url);
}

// ============================================================================
// SIGNED URL GENERATION
// ============================================================================

/**
 * Result type for photos with signed URLs added
 */
export interface SignedPhotoUrls {
  original_photo_path: string | null;
  thumbnail_photo_path: string | null;
  large_photo_path: string | null;
  original_photo_url: string | null;
  thumbnail_photo_url: string | null;
  large_photo_url: string | null;
  original_photo_url_expires_at: number | null;
  thumbnail_photo_url_expires_at: number | null;
  large_photo_url_expires_at: number | null;
}

/**
 * Input photo type - must have these URL fields (can be string or null)
 */
export interface PhotoWithUrls {
  original_photo_url?: string | null;
  thumbnail_photo_url?: string | null;
  large_photo_url?: string | null;
}

/**
 * Normalizes a storage path by trimming whitespace and removing leading slashes
 */
export const normalizePath = (path: string | null | undefined): string | null => {
  if (!path) return null;
  return path.trim().replace(/^\/+/, '');
};

/** The three stored size variants of a member photo. */
export type PhotoVariant = 'original' | 'thumbnail' | 'large';

/** Every variant — the historical behaviour, and the default. */
export const ALL_PHOTO_VARIANTS: readonly PhotoVariant[] = ['original', 'thumbnail', 'large'];

/**
 * The variants any list/grid surface can actually render: `thumbnail` (150px
 * avatars) and `large` (the card grid's 128px circles, and the error fallback
 * for either). `original` is only ever rendered on the member detail page and
 * in the photo modals, which fetch per member — signing it for a whole roster
 * is a third of the signing work for URLs nothing requests.
 */
export const LIST_PHOTO_VARIANTS: readonly PhotoVariant[] = ['thumbnail', 'large'];

/**
 * Generic function to add signed URLs to an array of photos
 *
 * @param photos - Array of photo objects with URL fields
 * @param bucketName - Supabase storage bucket name
 * @param variants - Which size variants to sign. Defaults to all three; pass
 *   `LIST_PHOTO_VARIANTS` from bulk/list callers so Storage isn't asked to mint
 *   JWTs for sizes the surface never renders. Unsigned variants come back as
 *   `null` URLs (their `_path` fields are still populated).
 * @returns Array of photos with signed URLs and expiry timestamps added
 *
 * @example
 * const photosWithSignedUrls = await addSignedUrlsToPhotos(
 *   rawPhotos,
 *   'member-webcam-photos'
 * );
 */
export async function addSignedUrlsToPhotos<T extends PhotoWithUrls>(
  photos: T[],
  bucketName: string,
  variants: readonly PhotoVariant[] = ALL_PHOTO_VARIANTS
): Promise<Array<T & SignedPhotoUrls>> {
  if (!photos.length) {
    return [];
  }

  const wanted = new Set(variants);

  // Extract and deduplicate all paths
  const paths = Array.from(
    new Set(
      photos.flatMap((photo) => [
        wanted.has('original') ? photo.original_photo_url : null,
        wanted.has('thumbnail') ? photo.thumbnail_photo_url : null,
        wanted.has('large') ? photo.large_photo_url : null,
      ])
        .filter((path): path is string => Boolean(path))
        .map((path) => normalizePath(path))
        .filter((path): path is string => Boolean(path))
    )
  );

  // Get signed URLs for all paths at once
  const signedUrlMap = await createSignedStorageUrls(bucketName, paths);

  // Map signed URLs back to each photo
  return photos.map((photo) => {
    const originalPath = normalizePath(photo.original_photo_url);
    const thumbnailPath = normalizePath(photo.thumbnail_photo_url);
    const largePath = normalizePath(photo.large_photo_url);

    const originalSigned = originalPath ? signedUrlMap[originalPath] : undefined;
    const thumbnailSigned = thumbnailPath ? signedUrlMap[thumbnailPath] : undefined;
    const largeSigned = largePath ? signedUrlMap[largePath] : undefined;

    return {
      ...photo,
      original_photo_path: originalPath,
      thumbnail_photo_path: thumbnailPath,
      large_photo_path: largePath,
      original_photo_url: originalSigned?.signedUrl ?? null,
      thumbnail_photo_url: thumbnailSigned?.signedUrl ?? null,
      large_photo_url: largeSigned?.signedUrl ?? null,
      original_photo_url_expires_at: originalSigned?.expiresAt ?? null,
      thumbnail_photo_url_expires_at: thumbnailSigned?.expiresAt ?? null,
      large_photo_url_expires_at: largeSigned?.expiresAt ?? null,
    };
  });
}
